import { type ButtonProps, ButtonWithLoader } from '@neo/primitives/button'
import { usePermissions } from 'v2/shared/hooks/use-permissions'
import type { RbacModules, RbacPermissionTypes } from 'v2/types/rbac'

interface ButtonWithPermissionProps extends ButtonProps {
  /**
   * The module to check permissions against
   */
  module?: RbacModules
  /**
   * The type of permission to check (e.g., 'create', 'view', 'edit', 'delete')
   */
  permissionType?: RbacPermissionTypes
  /**
   * Whether the button is in a loading state
   */
  loading?: boolean
  /**
   * Whether the button should be disabled if the user does not have permission
   */
  disabledOnNoPermission?: boolean
  /**
   * The text to display if the user does not have permission
   */
  disabledOnNoPermissionText?: string
}

export const ButtonWithPermission = ({
  module,
  permissionType,
  loading = false,
  disabledOnNoPermission = false,
  disabledOnNoPermissionText = 'You do not have permission to perform this action',
  children,
  ...props
}: ButtonWithPermissionProps) => {
  const { hasPermission } = usePermissions()
  const shouldRenderButton = !module || !permissionType || hasPermission(module, permissionType)

  // If the disabledOnNoPermission prop is false, we would hide the button if the user does not have permission
  if (!shouldRenderButton && !disabledOnNoPermission) return null

  return (
    <ButtonWithLoader
      loading={loading}
      disabled={disabledOnNoPermission && !shouldRenderButton}
      disabledReason={disabledOnNoPermission ? disabledOnNoPermissionText : undefined}
      {...props}
    >
      {children}
    </ButtonWithLoader>
  )
}
